/**
 * Gesperrte Namen für Nutzernamen, Parteinamen und Kürzel.
 *
 * Erkannt werden auch Schreibweisen mit Umlauten, Ziffern statt Buchstaben (H1tl3r), Sonderzeichen
 * dazwischen (N.S.D.A.P.) und verdoppelten Buchstaben (Hiiitler). Die Datenbank prüft mit derselben
 * Logik (Migration 0011, erzeugt mit `node scripts/gen-names-sql.mjs`). Bei Änderungen an den Listen
 * das Skript erneut ausführen und eine neue Migration anlegen.
 */

/** Begriffe, die nirgends im Namen vorkommen dürfen (auch als Teil eines Wortes). */
export const BLOCK_PARTS: string[] = [
  // Nationalsozialismus
  'hitler', 'adolf h', 'nsdap', 'nazi', 'goebbels', 'himmler', 'heydrich', 'eichmann', 'mengele', 'goering', 'bormann', 'hess rudolf', 'rudolf hess',
  'auschwitz', 'holocaust', 'shoah', 'hakenkreuz', 'swastika', 'siegheil', 'sieg heil', 'heilhitler', 'reichsfuehrer', 'fuehrerstaat', 'drittesreich', 'drittes reich',
  'endloesung', 'herrenrasse', 'untermensch', 'waffenss', 'waffen ss', 'totenkopf', 'wolfsschanze', 'reichskanzler adolf', 'blutundehre', 'blut und ehre',
  'whitepower', 'white power', 'whitepride', 'white pride', 'kukluxklan', 'ku klux', 'fourteenwords', 'fourteen words', 'rassenkrieg', 'judensau', 'judenrein',
  'volksverraeter', 'umvolkung', 'reichsbuerger',
  // Terror
  'alqaida', 'al qaida', 'alkaida', 'islamischerstaat', 'islamischer staat', 'taliban', 'hamas', 'hisbollah', 'hezbollah', 'raf kommando',
  // Beleidigungen und Herabwürdigungen
  'neger', 'nigger', 'nigga', 'kanake', 'zigeuner', 'schwuchtel', 'spast', 'fotze', 'hurensohn', 'wichser', 'arschloch', 'missgeburt', 'kinderficker', 'vergewaltig',
];

/** Kurze Zeichen, die nur als eigenes Wort gesperrt sind (sonst träfe „ss“ jede „Klasse“). */
export const BLOCK_WORDS: string[] = ['ss', 'sa', 'hh', '88', '18', '1488', '14 88', 'kkk', 'nsu', 'npd', 'isis', 'isil', 'nsd', 'ns', 'fuehrer', 'fuhrer'];

/** Erlaubte Wendungen, die sonst fälschlich träfen (werden vor der Prüfung entfernt). */
export const ALLOW: string[] = ['antinazi', 'gegennazi', 'nazisraus', 'nienazi'];

/** Kleinbuchstaben, Umlaute ausgeschrieben, ß zu ss. */
export function base(s: string): string {
  return (s || '').toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}
/** Ziffern und Zeichen, die als Buchstaben gelesen werden. */
const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '@': 'a', '$': 's', '!': 'i', '|': 'i' };
export const leet = (s: string): string => s.replace(/[0134578@$!|]/g, (c) => LEET[c]);
/** Nur Buchstaben, doppelte Buchstaben zusammengezogen. */
export const squash = (s: string): string => s.replace(/[^a-z]/g, '').replace(/(.)\1+/g, '$1');

const PARTS = [...new Set(BLOCK_PARTS.map((w) => squash(leet(base(w)))))].filter((w) => w.length >= 3);
const WORDS = [...new Set(BLOCK_WORDS.map((w) => base(w).replace(/\s+/g, ' ')))];

const ALLOW_N = ALLOW.map((w) => squash(leet(base(w))));
/** Teile, wie sie verglichen werden (für das SQL-Skript). */
export const NORMALIZED = { parts: PARTS, words: WORDS, allow: ALLOW_N };

/** Ist der Name gesperrt? */
export function nameBlocked(name: string): boolean {
  const b = base(name);
  let sq = squash(leet(b));
  for (const a of ALLOW_N) sq = sq.split(a).join('');
  if (PARTS.some((p) => sq.includes(p))) return true;
  const tokens = ' ' + b.replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  const joined = ' ' + b.replace(/[^a-z0-9]+/g, '') + ' ';
  return WORDS.some((w) => tokens.includes(' ' + w + ' ') || joined === ' ' + w.replace(/ /g, '') + ' ');
}

/** Fehlermeldung oder null. */
export const nameError = (name: string, what = 'Dieser Name'): string | null =>
  nameBlocked(name) ? `${what} ist nicht erlaubt. Bitte wähle einen anderen.` : null;
